<script setup lang="ts">
import { gsap } from 'gsap'
import type { BusinessTypeMeta } from '~/composables/useBusinessTypes'

const props = defineProps<{
  type: BusinessTypeMeta
}>()

const { t } = useI18n()
const localePath = useLocalePath()
const section = ref<HTMLElement | null>(null)

const typeName = computed(() => t(`page.home.one_platform.business_types.${props.type.i18nKey}`))

onMounted(() => {
  const el = section.value
  if (!el) return

  gsap.timeline({ defaults: { ease: 'power3.out' } })
    .fromTo(el.querySelector('.cs-icon'),
      { opacity: 0, scale: 0.6, rotation: -20 },
      { opacity: 1, scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(2)' })
    .fromTo(el.querySelector('.cs-title'),
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.6 }, '-=0.25')
    .fromTo(el.querySelector('.cs-subtitle'),
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.55 }, '-=0.3')
    .fromTo(el.querySelector('.cs-cta'),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5 }, '-=0.25')
})
</script>

<template>
  <section ref="section" class="min-h-[70vh] flex items-center py-24 md:py-28 bg-white relative overflow-hidden">
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true" />

    <div class="container relative text-center max-w-xl mx-auto">
      <div
        class="cs-icon inline-flex items-center justify-center size-16 md:size-20 rounded-2xl border-2 border-gray-900 shadow-[5px_5px_0_#111827] mb-6"
        :style="{ backgroundColor: type.accent }">
        <img :src="type.icon" :alt="typeName" class="size-9 md:size-11">
      </div>

      <h1 class="cs-title text-[clamp(1.75rem,4vw,2.75rem)] font-bold leading-[1.25] text-deep-indigo mb-4">
        {{ $t('page.business_types.coming_soon.title', { name: typeName }) }}
      </h1>

      <p class="cs-subtitle text-base md:text-lg text-overcast-night leading-relaxed mb-8">
        {{ $t('page.business_types.coming_soon.subtitle') }}
      </p>

      <div class="cs-cta flex flex-col sm:flex-row items-center justify-center gap-4">
        <a v-magnetic :href="localePath('/request-demo')" class="bfm-btn bfm-btn-md md:bfm-btn-lg bg-cheese-yellow">
          {{ $t('page.business_types.coming_soon.cta_primary') }}
        </a>
        <NuxtLink :to="localePath('/business-types')" class="text-gray-900 font-bold underline underline-offset-4 hover:text-sky-blue transition-colors">
          {{ $t('page.business_types.coming_soon.cta_secondary') }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
